import React, { Fragment } from 'react';
import moment from 'moment';
import some from 'lodash/some';
import { offsetDate } from 'pages/utils/time';
import { START_DATE, END_DATE } from '.';

export const getValidDatesInRange = ({ startDate, endDate, rangeStartDate, rangeEndDate }) => {
  let validStartDate = moment(startDate);
  let validEndDate = moment(endDate);

  if (rangeStartDate && startDate.isBefore(moment(rangeStartDate).startOf('day'))) {
    validStartDate = rangeStartDate;
  }

  if (rangeEndDate && endDate.isAfter(moment(rangeEndDate).endOf('day'))) {
    validEndDate = rangeEndDate;
  }

  if (
    (rangeStartDate && endDate.isBefore(moment(rangeStartDate).startOf('day'))) ||
    (rangeEndDate && startDate.isAfter(moment(rangeEndDate).endOf('day')))
  ) {
    validStartDate = undefined;
    validEndDate = undefined;
  }

  return { validStartDate, validEndDate };
};

export const isOutsideRange = (currentDay, availableStartDate, availableEndDate, offset) => {
  let day = moment(currentDay);
  if (offset) {
    day = offsetDate({ date: day, offset, keepTime: true });
  }

  if (availableStartDate && day.isBefore(moment(availableStartDate).startOf('day'))) return true;
  if (availableEndDate && day.isAfter(moment(availableEndDate).endOf('day'))) return true;
  return false;
};

export const ValidateDate = ({
  name,
  value,
  oppositeValue,
  availableStartDate,
  availableEndDate,
  dateFormat,
  single,
  offset,
  customValidation,
}) => {
  const type = {
    [START_DATE]: single ? 'date' : 'start date',
    [END_DATE]: 'end date',
  };

  let validatedDate = moment(value, dateFormat);
  let oppositeDate = moment(oppositeValue, dateFormat);

  if (single && name === END_DATE) {
    return null;
  }

  if (value === '') {
    return <Fragment>Select or enter {type[name]}.</Fragment>;
  }

  if (!validatedDate.isValid()) {
    return (
      <Fragment>
        Enter valid {type[name]} format ({dateFormat}).
      </Fragment>
    );
  }

  if (offset) {
    validatedDate = offsetDate({ date: validatedDate, offset, keepTime: true });
    oppositeDate = offsetDate({ date: oppositeDate, offset, keepTime: true });
  }

  if (
    (availableStartDate && validatedDate.isBefore(moment(availableStartDate).startOf('day'))) ||
    (availableEndDate && validatedDate.isAfter(moment(availableEndDate).endOf('day')))
  ) {
    return (
      <Fragment>
        Enter {type[name]} within calendar range:
        <br /> {availableStartDate.format(dateFormat)} - {availableEndDate.format(dateFormat)}
      </Fragment>
    );
  }

  if (!single) {
    if (name === START_DATE) {
      if (validatedDate.isAfter(oppositeDate)) {
        return (
          <Fragment>
            Enter {type[START_DATE]} before {type[END_DATE]}.
          </Fragment>
        );
      }
    } else if (validatedDate.isBefore(oppositeDate)) {
      return (
        <Fragment>
          Enter {type[END_DATE]} after {type[START_DATE]}.
        </Fragment>
      );
    }
  }

  if (customValidation) {
    let customValidationResult;

    if (name === START_DATE) {
      customValidationResult = customValidation({
        startDate: validatedDate,
        endDate: oppositeDate,
      });
    }

    if (name === END_DATE) {
      customValidationResult = customValidation({
        startDate: oppositeDate,
        endDate: validatedDate,
      });
    }

    if (customValidationResult) {
      return customValidationResult;
    }
  }

  return null;
};

export const validate = ({
  availableStartDate,
  availableEndDate,
  dateFormat,
  single,
  offset,
  customValidation,
}) => values => {
  const errors = {};
  const startDateValidation = ValidateDate({
    name: START_DATE,
    value: values[START_DATE],
    oppositeValue: values[END_DATE],
    availableStartDate,
    availableEndDate,
    dateFormat,
    single,
    offset,
    customValidation,
  });

  const endDateValidation = ValidateDate({
    name: END_DATE,
    value: values[END_DATE],
    oppositeValue: values[START_DATE],
    availableStartDate,
    availableEndDate,
    dateFormat,
    single,
    offset,
    customValidation,
  });

  if (startDateValidation) {
    errors[START_DATE] = startDateValidation;
  }

  if (endDateValidation) {
    errors[END_DATE] = endDateValidation;
  }

  return errors;
};

export const isPresetEqual = ({ preset, otherPreset }) =>
  !some(
    preset,
    (value, key) =>
      !otherPreset[key] ||
      value.label !== otherPreset[key].label ||
      value.startDate.format() !== otherPreset[key].startDate.format() ||
      value.endDate.format() !== otherPreset[key].endDate.format() ||
      value.divider !== otherPreset[key].divider
  );
